<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter, type RouteLocationRaw } from 'vue-router';
import type { ContractGenerationTemplateInDb } from '@/api';
import TemplateCreateModal from '@/components/contractGeneration/templates/TemplateCreateModal.vue';
import TemplatingBreadcrumb from '@/components/contractGeneration/TemplatingBreadcrumb.vue';
import { useContractGenerationStore } from '@/stores/contractGenerationStore';
import { useListQuerySync } from '@/composables/useListQuerySync';
import Layout from '@/components/design/layout/Layout.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaCountLabel from '@/components/design/base/GaiaCountLabel.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaTooltip from '@/components/design/base/GaiaTooltip.vue';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import GaiaDataTable, {
  type GaiaDataColumn,
} from '@/components/design/composite/GaiaDataTable.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaPagination from '@/components/design/composite/GaiaPagination.vue';
import GaiaTableRowState from '@/components/design/composite/GaiaTableRowState.vue';
import GaiaTableSkeleton from '@/components/design/composite/GaiaTableSkeleton.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';
import {
  gaiaContentWidth,
  gaiaFontSize,
  gaiaFontWeight,
  gaiaNeutral,
  gaiaStatus,
} from '@/components/design/themes/tokens';
import { templateRoute } from '@/router/links';

const router = useRouter();
const message = useGaiaToast();
const store = useContractGenerationStore();

const { searchQuery: searchText, currentPage, pageSize } = useListQuerySync();
const createModalVisible = ref<boolean>(false);

async function loadTemplates(): Promise<void> {
  await store.fetchTemplates({
    text: searchText.value.trim(),
    page: currentPage.value,
    pageSize: pageSize.value,
  });
}

// New query → restart from page 1; the page watcher then refetches. The field
// owns the debounce.
function applySearch(): void {
  if (currentPage.value === 1) {
    void loadTemplates();
  } else {
    currentPage.value = 1;
  }
}

function clearSearch(): void {
  searchText.value = '';
  applySearch();
}

watch(currentPage, () => {
  void loadTemplates();
});

watch(pageSize, () => {
  void loadTemplates();
});

const hasQuery = computed(() => searchText.value.trim().length > 0);

const countLabel = computed(() => {
  const count = store.totalCount;
  return `${count} ${count === 1 ? 'template' : 'templates'}`;
});

// ── Delete: inline row states instead of a popconfirm ────────────────────────
// The row cycles normal → confirm strip → busy strip → (error strip on
// failure). Success removes it from the store, so there is no "done" state.

const confirmUid = ref<string | null>(null);
const deletingUid = ref<string | null>(null);
const rowErrors = ref<Record<string, string>>({});

function askDelete(template: ContractGenerationTemplateInDb): void {
  confirmUid.value = template.uid;
}

async function doDelete(template: ContractGenerationTemplateInDb): Promise<void> {
  confirmUid.value = null;
  dismissError(template.uid);
  deletingUid.value = template.uid;
  try {
    await store.deleteTemplate(template.uid);
    message.success(`Template "${template.name}" deleted.`);
  } catch (error) {
    rowErrors.value = {
      ...rowErrors.value,
      [template.uid]: error instanceof Error ? error.message : 'Failed to delete template.',
    };
  } finally {
    deletingUid.value = null;
  }
}

function dismissError(uid: string): void {
  const next = { ...rowErrors.value };
  delete next[uid];
  rowErrors.value = next;
}

function detailRoute(template: ContractGenerationTemplateInDb): RouteLocationRaw {
  return templateRoute(template.uid);
}

function openDetail(template: ContractGenerationTemplateInDb): void {
  void router.push(detailRoute(template));
}

function startWizard(template: ContractGenerationTemplateInDb): void {
  void router.push({
    name: 'contract-generation-wizard',
    params: { templateUid: template.uid },
  });
}

function handleCreated(template: ContractGenerationTemplateInDb): void {
  store.upsertTemplate(template);
  openDetail(template);
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

onMounted(() => {
  void loadTemplates();
});

// The mock's own column template, verbatim.
const tableColumns: GaiaDataColumn[] = [
  { key: 'name', label: 'Name', width: 'minmax(220px, 1.2fr)' },
  { key: 'description', label: 'Description', width: 'minmax(200px, 1.4fr)' },
  { key: 'updated_at', label: 'Updated', width: '150px' },
];

const skeletonCells = ['62%', '80%', '96px'];

// --- chrome ----------------------------------------------------------------
const rowBorder = gaiaNeutral.borderSoft;
const rowHover = 'rgba(25,27,31,.025)';
const dangerColor = gaiaStatus.alert;
const dangerHover = gaiaStatus.alertHover;
const iconColor = gaiaNeutral.textSubtle;
const inkColor = gaiaNeutral.ink;
const nameSize = gaiaFontSize.row;
const nameWeight = String(gaiaFontWeight.semibold);
</script>

<template>
  <Layout :content-max-width="gaiaContentWidth.table">
    <template #breadcrumb>
      <templating-breadcrumb />
    </template>
    <n-flex vertical :size="18">
      <!-- Title + count + subtitle on the left, search and the primary action
           pushed right — the CM list-page header row. -->
      <n-flex align="flex-start" :size="[20, 12]">
        <n-flex vertical :size="6" :style="{ flex: '1 1 240px', minWidth: 0 }">
          <n-flex align="baseline" :size="10" :style="{ minWidth: 0 }">
            <gaia-header :level="1" variant="display" size="screen" :style="{ margin: 0 }">
              Contract Templates
            </gaia-header>
            <gaia-count-label v-if="!store.loading">{{ countLabel }}</gaia-count-label>
          </n-flex>
          <gaia-text size="row" tone="tertiary">
            Upload .docx templates with placeholders for reuse.
          </gaia-text>
        </n-flex>

        <n-flex align="center" justify="end" :size="10" :style="{ marginLeft: 'auto' }">
          <gaia-search-input
            v-model:value="searchText"
            variant="toolbar"
            placeholder="Search templates"
            :style="{ width: '288px' }"
            @search="applySearch"
          />
          <gaia-button
            type="primary"
            size="medium"
            data-tour="tpl.create-button"
            @click="createModalVisible = true"
          >
            <template #icon>
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              >
                <path d="M12 5v14M5 12h14" />
              </svg>
            </template>
            Create template
          </gaia-button>
        </n-flex>
      </n-flex>

      <gaia-data-table data-tour="tpl.list" :columns="tableColumns" :action-width="128">
        <template #default="{ rowStyle }">
          <gaia-table-skeleton
            v-if="store.loading"
            :row-style="rowStyle"
            :cells="skeletonCells"
            :rows="6"
          />

          <gaia-empty-state v-else-if="store.error" status="alert" title="Couldn’t load templates">
            The server didn’t respond in time. Nothing was changed — retrying is safe.
            <template #meta>{{ store.error }}</template>
            <template #actions>
              <gaia-button type="primary" size="small" @click="() => loadTemplates()">
                Retry
              </gaia-button>
            </template>
          </gaia-empty-state>

          <gaia-empty-state
            v-else-if="store.templates.length === 0 && hasQuery"
            :title="`No templates match “${searchText.trim()}”`"
          >
            <template #icon>
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
              >
                <circle cx="8" cy="8" r="5" />
                <path d="M11.8 11.8 15.5 15.5" />
              </svg>
            </template>
            Search covers template names and descriptions. Try a shorter term, or clear the search.
            <template #actions>
              <gaia-button size="small" @click="clearSearch">Clear search</gaia-button>
            </template>
          </gaia-empty-state>

          <gaia-empty-state v-else-if="store.templates.length === 0" title="No templates yet">
            <template #icon>
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M4 2.5h6l4 4v9H4z" />
                <path d="M10 2.5v4h4M7 10h4M7 12.5h3" />
              </svg>
            </template>
            Upload a .docx with placeholders and GAIA extracts its fields automatically — every
            document drafted from it starts from the same source.
            <template #actions>
              <gaia-button type="primary" size="small" @click="createModalVisible = true">
                Create template
              </gaia-button>
            </template>
          </gaia-empty-state>

          <template v-for="row in store.templates" v-else :key="row.uid">
            <gaia-table-row-state
              v-if="confirmUid === row.uid"
              variant="confirm"
              :title="`Delete “${row.name}”?`"
              confirm-label="Delete template"
              @cancel="confirmUid = null"
              @confirm="doDelete(row)"
            >
              All of its variants are removed too, and the drafts made from it are deleted.
              Documents already saved are kept.
            </gaia-table-row-state>

            <gaia-table-row-state
              v-else-if="deletingUid === row.uid"
              variant="busy"
              note="Undo not available"
            >
              Deleting “{{ row.name }}”…
            </gaia-table-row-state>

            <gaia-table-row-state
              v-else-if="rowErrors[row.uid]"
              variant="error"
              @retry="doDelete(row)"
              @dismiss="dismissError(row.uid)"
            >
              {{ rowErrors[row.uid] }}
            </gaia-table-row-state>

            <div v-else class="row" :style="rowStyle" @click="openDetail(row)">
              <!-- A real link so the tour can click it (`.row .name-link`). -->
              <router-link :to="detailRoute(row)" class="name-link" @click.stop>
                {{ row.name }}
              </router-link>
              <gaia-text truncate size="row" tone="tertiary">
                {{ row.description ?? '—' }}
              </gaia-text>
              <gaia-text
                size="dense"
                tone="tertiary"
                :style="{ fontVariantNumeric: 'tabular-nums' }"
              >
                {{ formatDate(row.updated_at ?? row.created_at) }}
              </gaia-text>

              <n-flex justify="end" :size="4" :wrap="false" @click.stop>
                <gaia-tooltip>
                  <template #trigger>
                    <gaia-icon-button
                      :dimension="28"
                      quaternary
                      class="inline-action"
                      @click="startWizard(row)"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="M8.8 1.8 3.2 9h4l-.8 5.2L12.8 7h-4z" />
                      </svg>
                    </gaia-icon-button>
                  </template>
                  Draft document
                </gaia-tooltip>

                <gaia-tooltip>
                  <template #trigger>
                    <gaia-icon-button
                      :dimension="28"
                      quaternary
                      class="inline-action"
                      @click="openDetail(row)"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="M3 13.2 12.2 4l-1.7-1.7L1.3 11.5 1 14z" />
                      </svg>
                    </gaia-icon-button>
                  </template>
                  Edit template
                </gaia-tooltip>

                <gaia-tooltip>
                  <template #trigger>
                    <gaia-icon-button
                      :dimension="28"
                      quaternary
                      class="inline-action danger"
                      @click="askDelete(row)"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="M2.5 4h11M5.5 4V2.5h5V4M4 4l.7 9.5h6.6L12 4M6.5 6.8v4M9.5 6.8v4" />
                      </svg>
                    </gaia-icon-button>
                  </template>
                  Delete template
                </gaia-tooltip>
              </n-flex>
            </div>
          </template>
        </template>

        <template #footer>
          <gaia-pagination
            :page="currentPage"
            :page-size="pageSize"
            :total="store.totalCount"
            :page-sizes="[5, 10, 25, 50]"
            @update:page="(page: number) => (currentPage = page)"
            @update:page-size="(size: number) => (pageSize = size)"
          />
        </template>
      </gaia-data-table>
    </n-flex>

    <TemplateCreateModal v-model:show="createModalVisible" @created="handleCreated" />
  </Layout>
</template>

<style scoped>
/* A body row rides the grid template GaiaDataTable hands out through the slot,
   so only the row's own chrome lives here. */
.row {
  display: grid;
  align-items: center;
  gap: 16px;
  height: 56px;
  padding: 0 18px;
  border-bottom: 1px solid v-bind(rowBorder);
  cursor: pointer;
}

.row:hover {
  background: v-bind(rowHover);
}

/* The name is the record link — bold ink, not link-blue, per the mock. */
.name-link {
  min-width: 0;
  padding: 0;
  border: none;
  background: none;
  font-family: inherit;
  font-size: v-bind(nameSize);
  font-weight: v-bind(nameWeight);
  color: v-bind(inkColor);
  text-decoration: none;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
}

.inline-action {
  color: v-bind(iconColor);
}

.inline-action.danger {
  color: v-bind(dangerColor);
}

.inline-action.danger:hover {
  background: v-bind(dangerHover);
}
</style>
